import {useMemo} from 'react';

import {type Point} from '../../util/plotHelpers/types';
import {
  type NumericLine,
  presentNumericLines,
  type RenderLine,
} from '../vis/chartSeries';

/**
 * Configuration for line filtering based on chart state.
 */
export type LineFilterConfig = {
  /** Scale type for the x-axis - affects which points are filtered */
  xScale: 'linear' | 'log';
  /** Scale type for the y-axis - affects which points are filtered */
  yScale: 'linear' | 'log';
  /** Whether this is the full-fidelity (BRSDQ) rendering path */
  isFullFidelity: boolean;
  /** Whether any chart element is currently being hovered */
  isHovered: boolean;
};

/**
 * Return type for useLinesForDisplay hook.
 */
export type LinesForDisplayResult = {
  /** Lines after scale filtering and baseline styling applied */
  filteredLines: RenderLine[];
  filteredNumericLines: NumericLine[];
};

// ============================================================================
// PURE TRANSFORMATION FUNCTIONS
// ============================================================================

/** Point-bearing input; filtering preserves the caller's remaining fields. */
type LineWithPoints = {data: Point[]};

/**
 * Shared visibility predicate for chart filtering and zoom telemetry.
 *
 * Filtering rules:
 * - x log scale: filter x <= 0 and non-finite x
 * - y log scale: filter y <= 0
 * - Sampled path: also filter non-finite x and y
 * - Full fidelity + both linear: no filtering (backend handles it)
 */
export function getPointScaleFilter(
  xScale: 'linear' | 'log',
  yScale: 'linear' | 'log',
  isFullFidelity: boolean
): (point: Point) => boolean {
  return (p: Point): boolean => {
    // x-axis checks
    if (xScale === 'log') {
      if (!Number.isFinite(p.x) || p.x <= 0) {
        return false;
      }
    } else if (!isFullFidelity && !Number.isFinite(p.x)) {
      return false;
    }

    // y-axis checks
    if (yScale === 'log') {
      if (p.y <= 0) {
        return false;
      }
    } else if (!isFullFidelity && !Number.isFinite(p.y)) {
      return false;
    }

    return true;
  };
}

/** Filter lazily: unchanged lines keep their arrays and the full-fidelity linear path skips scanning. */
export function filterPointsByScale<T extends LineWithPoints>(
  lines: T[],
  xScale: 'linear' | 'log',
  yScale: 'linear' | 'log',
  isFullFidelity: boolean
): T[] {
  // Fast path: full fidelity with both linear scales needs no filtering
  if (isFullFidelity && xScale === 'linear' && yScale === 'linear') {
    return lines;
  }

  const shouldKeep = getPointScaleFilter(xScale, yScale, isFullFidelity);

  let filteredLines: T[] | undefined;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const firstRemoved = line.data.findIndex(point => !shouldKeep(point));
    if (firstRemoved === -1) {
      continue;
    }

    // Copy only after a rejected point; valid lines keep their existing arrays.
    const filtered = line.data.slice(0, firstRemoved);
    for (let j = firstRemoved + 1; j < line.data.length; j++) {
      if (shouldKeep(line.data[j])) {
        filtered.push(line.data[j]);
      }
    }
    filteredLines ??= lines.slice();
    filteredLines[i] = {...line, data: filtered};
  }
  return filteredLines ?? lines;
}

/**
 * Pure function to apply baseline line styling based on hover state.
 * Returns new line objects for baselines, unchanged references for others.
 *
 * - When NOT hovered: baseline runs show as solid (default)
 * - When hovered: baseline runs show as dashed (distinguishes from hovered run)
 *
 * @param lines - Lines to process
 * @param isHovered - Whether the chart is being hovered
 * @returns Lines with baseline styling applied (new objects for modified lines)
 */
export function applyBaselineHoverStyles(
  lines: RenderLine[],
  isHovered: boolean
): RenderLine[] {
  // Fast path: no baselines means no changes needed.
  // Returns same reference so React can skip downstream re-renders.
  const hasBaselines = lines.some(line => line.isBaseline);
  if (!hasBaselines) {
    return lines;
  }

  return lines.map(line => {
    if (line.isBaseline) {
      return {
        ...line,
        mark: isHovered ? 'dashed' : 'solid',
      };
    }
    return line;
  });
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Filter numeric snapshots independently from presentation, then apply hover style.
 * Rejoining descriptions does not scan points, and hover preserves the numeric result.
 */
export function useLinesForDisplay(
  lines: NumericLine[],
  presentation: RenderLine[],
  config: LineFilterConfig
): LinesForDisplayResult {
  const {xScale, yScale, isFullFidelity, isHovered} = config;

  // STAGE 1: Static filtering (expensive)
  // Only re-runs when lines or scale configuration changes
  const linesWithValidPoints = useMemo(
    () => filterPointsByScale(lines, xScale, yScale, isFullFidelity),
    [lines, xScale, yScale, isFullFidelity]
  );

  // STAGE 2: Dynamic styling (cheap)
  // Re-runs when hover state changes, but only processes baseline lines
  const presentedLines = useMemo(
    () => presentNumericLines(linesWithValidPoints, presentation),
    [linesWithValidPoints, presentation]
  );
  const filteredLines = useMemo(
    () => applyBaselineHoverStyles(presentedLines, isHovered),
    [presentedLines, isHovered]
  );

  return {filteredLines, filteredNumericLines: linesWithValidPoints};
}
